import React, { useEffect, useState } from "react"
import { useQuery } from "@apollo/client"
import styled from "styled-components"
import { ModalFooter } from "reactstrap"
import { Paragraph, Tel } from "@pga/pga-component-library"
import { redirectToLogout } from "../../lib/auth"
import { Button } from "../Button"
import { Modal, ModalBody, Title } from "../Modal"
import { path } from "ramda"
import { isAffiliate, isAssociate, isStudent, pgaComTld } from "../utils"
import { TERMINATE_CODE } from "./terminateCodes"
import "./modal.css"
import { COACH_TOOLS_API } from "../../apolloClient"
import { flags, useFeatureFlags } from "../../lib/feature-flags"
import { redirectToMemberApplications } from "../../lib/ptmApplicationsRedirect"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/associateApplicationEligibility"
import { AFFILIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/affiliateApplicationEligibility"
import BecomeAssociateModal from "../widgets/CourseCurriculum/BecomeAssociateModal"
import PtmApplicationStatus from "../PtmApplicationStatus"
import { PTM_MEMBERSHIP_APPLICATIONS_QUERY } from "../../utils/prospect/withPtmApplicationsQuery"
import {
  isDeniedPtmApplication,
  pickLatestApplicationForType,
  PTM_APPLICATION_TYPES,
  shouldReplaceApplyCtaWithStatus,
} from "../../utils/prospect/ptmApplicationStatus"

const ModalWrapper = styled(Modal)`
  .modal-content {
    border-radius: 0;
    border: none;
    @media screen and (max-width: 575.98px) {
      text-align: center;
    }
  }
  .modal-footer {
    width: 100%;
    justify-content: center;
    padding-top: 1.5rem;
    padding-bottom: 0;
  }
`

const Body = styled(ModalBody)`
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Telephone = styled(Tel)`
  font-size: 24px;
  margin-bottom: 24px;
`

const ApplicationStatusBlock = styled.div`
  width: 100%;
  margin-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const MEMBERSHIP_NUMBER = "(800) 474-2776"

/** Terminated auth `me` may lack program type; mirror useMembershipJourney fallback. */
const meWithTerminationTypeFallback = me => {
  if (!me) return me
  const programType = me.mainProgramTypeCode || me.type
  if (programType) return me
  const terminationType = me.termination?.type
  if (!terminationType) return me
  return {
    ...me,
    type: terminationType === "AF" ? "AP" : terminationType,
  }
}

const pathActionState = (application, eligibilityEligible) => {
  const replaceApplyCta = shouldReplaceApplyCtaWithStatus(application?.status)
  const showDeniedStatus = isDeniedPtmApplication(application?.status)
  // Denied is not a blocking eligibility status on the backend; keep CTA when eligible.
  const showApplyCta = !replaceApplyCta && eligibilityEligible === true
  const showStatus = replaceApplyCta || showDeniedStatus

  return { replaceApplyCta, showDeniedStatus, showApplyCta, showStatus }
}

const TerminatedModal = ({ me, duesCycle }) => {
  const [open] = useState(true)
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)
  const [isPtmApplicationsRedirectEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_PTM_APPLICATIONS_REDIRECT,
  ])
  const redirectEnabled = !!isPtmApplicationsRedirectEnabled

  const duesTermination =
    path(["termination", "terminateCd"])(me) === TERMINATE_CODE.NON_PAYMENT
  const reEstablishmentFee = duesCycle?.order?.lineItems?.some(
    item => item?.name === "Re-Establishment Fee",
  )
  const isDuesReestablishmentBranch = duesTermination && reEstablishmentFee
  // Non-payment terminations should not surface application CTAs (pay dues path).
  const suppressApplicationCtas = duesTermination

  const eligibilityMe = meWithTerminationTypeFallback(me)
  const studentViewer = isStudent(eligibilityMe)
  const associateViewer = isAssociate(eligibilityMe)
  const affiliateViewer = isAffiliate(eligibilityMe)
  const shouldFetchAssociateEligibility =
    redirectEnabled &&
    !suppressApplicationCtas &&
    (studentViewer || associateViewer || affiliateViewer)
  const shouldFetchAffiliateEligibility =
    redirectEnabled &&
    !suppressApplicationCtas &&
    (studentViewer || associateViewer)
  const shouldFetchPtmApplications =
    redirectEnabled &&
    !suppressApplicationCtas &&
    (shouldFetchAssociateEligibility || shouldFetchAffiliateEligibility)

  const {
    data: associateEligibilityData,
    loading: associateEligibilityLoading,
    error: associateEligibilityError,
  } = useQuery(ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchAssociateEligibility,
  })

  const {
    data: affiliateEligibilityData,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
  } = useQuery(AFFILIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchAffiliateEligibility,
  })

  const {
    data: ptmApplicationsData,
    loading: ptmApplicationsLoading,
    error: ptmApplicationsError,
  } = useQuery(PTM_MEMBERSHIP_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchPtmApplications,
  })

  const membershipApplications = ptmApplicationsError
    ? []
    : ptmApplicationsData?.membershipApplications || []
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const affiliateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.AFFILIATE,
  )

  const associateEligible =
    shouldFetchAssociateEligibility &&
    !associateEligibilityLoading &&
    !associateEligibilityError &&
    associateEligibilityData?.associateApplicationEligibility?.eligible === true

  const affiliateEligible =
    shouldFetchAffiliateEligibility &&
    !affiliateEligibilityLoading &&
    !affiliateEligibilityError &&
    affiliateEligibilityData?.affiliateApplicationEligibility?.eligible === true

  const applicationsReady =
    !shouldFetchPtmApplications ||
    (!ptmApplicationsLoading && (ptmApplicationsData || ptmApplicationsError))

  const associateAction =
    applicationsReady &&
    pathActionState(associateApplication, associateEligible)
  const affiliateAction =
    applicationsReady &&
    pathActionState(affiliateApplication, affiliateEligible)

  const showBecomeAssociateButton = Boolean(associateAction?.showApplyCta)
  const showBecomeAffiliateButton = Boolean(affiliateAction?.showApplyCta)
  const showAnyApplicationCta =
    showBecomeAssociateButton || showBecomeAffiliateButton

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  return (
    <>
      <ModalWrapper isOpen={open} toggle={() => {}} size="md" centered>
        {isDuesReestablishmentBranch ? (
          <Body>
            <Title className="mb-4">Membership Not Current</Title>
            <Paragraph fw="normal" className="mb-2 ml-4">
              Pay your outstanding dues to re-establish your membership.
            </Paragraph>
            <Paragraph fw="normal" className="ml-4">
              For additional support contact PGA Membership Services at:
            </Paragraph>
            <Telephone phoneNumber={MEMBERSHIP_NUMBER} />
            <ModalFooter>
              <Button
                href={`https://${pgaComTld}/member/dues/overview`}
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
              >
                Pay Dues Now
              </Button>
              <Button color="primary" onClick={() => redirectToLogout()}>
                Return to Public Content
              </Button>
            </ModalFooter>
          </Body>
        ) : (
          <Body>
            <Title className="mb-4">Membership Not Current</Title>
            <Paragraph fw="normal" className="mb-2">
              Your membership is{" "}
              <Paragraph fw="normal" className="d-inline text-danger">
                <u>not current.</u>
              </Paragraph>
            </Paragraph>
            <Paragraph fw="normal">
              Please contact PGA Membership Services at:
            </Paragraph>
            <Telephone phoneNumber={MEMBERSHIP_NUMBER} />
            {(associateAction?.showStatus || affiliateAction?.showStatus) && (
              <ApplicationStatusBlock>
                {associateAction?.showStatus ? (
                  <PtmApplicationStatus
                    status={associateApplication.status}
                    date={
                      associateAction.showDeniedStatus
                        ? associateApplication.updatedAt
                        : undefined
                    }
                    applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
                    centered
                  />
                ) : null}
                {affiliateAction?.showStatus ? (
                  <PtmApplicationStatus
                    status={affiliateApplication.status}
                    date={
                      affiliateAction.showDeniedStatus
                        ? affiliateApplication.updatedAt
                        : undefined
                    }
                    applicationType={PTM_APPLICATION_TYPES.AFFILIATE}
                    centered
                  />
                ) : null}
              </ApplicationStatusBlock>
            )}
            <ModalFooter>
              {showBecomeAssociateButton && (
                <Button
                  color="primary"
                  className="m-2"
                  onClick={() => setIsBecomeAssociateModalOpen(true)}
                >
                  Become Associate
                </Button>
              )}
              {showBecomeAffiliateButton && (
                <Button
                  color="primary"
                  className="m-2"
                  onClick={redirectToMemberApplications}
                >
                  Become Affiliate
                </Button>
              )}
              <Button
                color={showAnyApplicationCta ? "secondary" : "primary"}
                outline={showAnyApplicationCta}
                onClick={() => redirectToLogout()}
              >
                Return to Public Content
              </Button>
            </ModalFooter>
          </Body>
        )}
      </ModalWrapper>
      <BecomeAssociateModal
        isOpen={isBecomeAssociateModalOpen}
        onClose={() => setIsBecomeAssociateModalOpen(false)}
        onApply={redirectToMemberApplications}
      />
    </>
  )
}

export default TerminatedModal
